import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { concatMap, debounceTime, Subject } from 'rxjs';
import { ModulesEnum } from '../enums/modules.enum';
import {
  normalizeVisibleColumnIds,
  resolveListExtrasColumnCount,
  sanitizeDraftColumnIds,
} from '../helpers/list-column-preferences.helper';
import { ModuleListExtraColumnsConfig } from '../interfaces/shared/module-list-extra-columns-config.interface';
import {
  LIST_COLUMNS_SETTINGS_KEY,
  listColumnPreferencesToBagValue,
  parseListColumnPreferences,
} from '../mappers/list-column-preferences.mapper';
import { UserAppSettingsStore } from './user-app-settings.store';

type ListColumnPreferencesState = {
  preferences: Partial<Record<ModulesEnum, string[]>>;
};

const DEFAULT_STATE: ListColumnPreferencesState = {
  preferences: {},
};

export const ListColumnPreferencesStore = signalStore(
  { providedIn: 'root' },
  withState(DEFAULT_STATE),
  withMethods(
    (
      store,
      userAppSettingsStore = inject(UserAppSettingsStore),
      destroyRef = inject(DestroyRef),
    ) => {
      const persist$ = new Subject<void>();
      persist$
        .pipe(
          debounceTime(300),
          concatMap(() => {
            const settingsValues = {
              ...(userAppSettingsStore.userAppSettings?.()?.settingsValues ??
                {}),
              [LIST_COLUMNS_SETTINGS_KEY]: listColumnPreferencesToBagValue(
                store.preferences(),
              ),
            };
            return userAppSettingsStore.replaceSettings({ settingsValues });
          }),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe();

      const persist = () => persist$.next();

      return {
        visibleColumnIds(
          module: ModulesEnum,
          config: ModuleListExtraColumnsConfig,
        ): string[] {
          return normalizeVisibleColumnIds(store.preferences()[module], config);
        },
        listExtrasColumnCount(
          module: ModulesEnum,
          config: ModuleListExtraColumnsConfig | undefined,
        ): number {
          return resolveListExtrasColumnCount(
            config,
            store.preferences()[module],
          );
        },
        setVisibleColumnIds(
          module: ModulesEnum,
          columnIds: string[],
          config: ModuleListExtraColumnsConfig,
        ): void {
          const normalized = sanitizeDraftColumnIds(columnIds, config);
          patchState(store, {
            preferences: {
              ...store.preferences(),
              [module]: normalized,
            },
          });
          persist();
        },
        resetToDefault(module: ModulesEnum): void {
          const { [module]: _removed, ...rest } = store.preferences();
          patchState(store, { preferences: rest });
          persist();
        },
        hydrateFromBag(
          settingsValues: Record<string, unknown> | undefined,
        ): void {
          patchState(store, {
            preferences: parseListColumnPreferences(
              settingsValues?.[LIST_COLUMNS_SETTINGS_KEY],
            ),
          });
        },
      };
    },
  ),
);
